import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Seo } from "../components/Seo";
import { PageHeader, Kicker, CTAButton } from "../components/Bits";
import { Reveal } from "../components/Reveal";
import { EVENT, LINKS, IMAGES } from "../lib/site";

const CHAPTERS = [
  {
    n: "01",
    title: "It started with a neighbourhood jog.",
    body: "A handful of friends wanted to shake off the Thanksgiving feast and do a little good. One lap of Casablanca Beach later, the On The Lake Turkey Trot was born, equal parts fun run and community celebration.",
  },
  {
    n: "02",
    title: "The lake is our course, and our character.",
    body: "Our flat, chip-timed 5 km hugs the Lake Ontario shoreline in Grimsby. It is fast enough for PBs and gentle enough for strollers, wagons and first-timers alike.",
  },
  {
    n: "03",
    title: "Every stride supports inclusion.",
    body: `100% of net proceeds go to ${EVENT.charity}, funding inclusive programs and employment support for people of all abilities across our region.`,
  },
  {
    n: "04",
    title: "Fuel up at the finish.",
    body: "Cross the line to post-race snacks for a well-earned energy boost, cheer on every finisher, and soak up the community spirit on the shore. Bring the whole flock!",
  },
];

const FOUNDER_NOTE = [
  "Ever since I started racing, I’ve been amazed by the way running brings people together. Watching families and friends show up to support one another has always been one of my favourite parts of the sport.",
  "I run for many reasons, but what I value most is the community that running creates. Sharing the road with people who have the same passion, excitement, and goals as you is something truly special.",
  "Last year, I ran the Buffalo Turkey Trot, and it was one of the best race experiences I’ve ever had. Hundreds of people lined up at the start, all with the same simple goal: to finish. Along the 8 km course, the streets were filled with families, friends, and complete strangers cheering everyone on. The energy was incredible.",
  "On my way home, I kept thinking: Why don’t we have something like this in Grimsby?",
  "There wasn’t a local race that felt like it was truly for everyone, a place where experienced runners could chase a personal best, beginners could take on their first 5K, families could come out together, and everyone could simply have fun.",
  "Running has always been about community, and I believe we can create that same feeling here in Grimsby. It may start small, but I can already imagine how much this race could grow and the community we could build around it, starting with one simple race.",
  "At the same time, I’ve always wanted to find a way to give back to the community, but I never really knew how. Then I realized that the thing I do every single day, running, could be one of the best ways for me to make a difference.",
  "The Turkey Trot is my way of combining something I’m incredibly passionate about with something bigger than myself. It gives us an opportunity to bring our community together, create a new Thanksgiving tradition, and raise money to support Inclusion West Niagara.",
  "I hope this race becomes more than just an event. I hope it becomes something our community looks forward to every Thanksgiving, something that brings people together, gets families moving, supports a great cause, and reminds us what can happen when a community comes together.",
  "This is just the beginning. I can’t wait to see you at the start line.",
];

const FOUNDER_IMG = `${process.env.PUBLIC_URL}/media/founder.jpg`;

export default function About() {
  return (
    <>
      <Seo title="About the Race" path="/about" description={`The story of the ${EVENT.name} and how it supports ${EVENT.charity}.`} />
      <PageHeader
        chapter="about the race"
        title="A little run with a lot of heart."
        sub="It's our very first year! The inaugural Grimsby On The Lake Turkey Trot brings families, runners and neighbours together for one unforgettable autumn morning."
        image={IMAGES.course}
      />

      <section className="mx-auto max-w-7xl px-5 sm:px-8 pb-8">
        {CHAPTERS.map((c, i) => (
          <Reveal key={c.n}>
            <div className="grid gap-6 md:grid-cols-12 py-12 border-t border-ink/10 items-start">
              <div className="md:col-span-3">
                <span className="font-accent text-6xl text-terracotta">{c.n}</span>
              </div>
              <div className="md:col-span-9 max-w-2xl">
                <h2 className="font-display font-semibold tracking-tight text-3xl sm:text-4xl">{c.title}</h2>
                <p className="mt-4 text-base sm:text-lg text-ink-soft leading-relaxed">{c.body}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </section>

      <section className="bg-cream-200 py-24 sm:py-32" data-testid="founder-note">
        <div className="mx-auto max-w-3xl px-5 sm:px-8">
          <Reveal>
            <Kicker>from the founder</Kicker>
            <h2 className="mt-2 font-display font-semibold tracking-tight text-4xl sm:text-5xl">
              A note from the Race Founder &amp; Director
            </h2>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="mt-8 space-y-5 text-base sm:text-lg text-ink-soft leading-relaxed">
              {FOUNDER_NOTE.map((p, i) => (
                <p
                  key={i}
                  className={i === 0 ? "first-letter:font-display first-letter:text-6xl first-letter:font-semibold first-letter:text-terracotta first-letter:float-left first-letter:mr-3 first-letter:leading-[0.8]" : ""}
                >
                  {p}
                </p>
              ))}
            </div>

            <div className="mt-10 border-t border-ink/10 pt-8 flex items-center gap-5">
              <img
                src={FOUNDER_IMG}
                alt="Luc DiFlavio, Race Founder & Director"
                className="w-20 h-24 sm:w-24 sm:h-28 object-cover rounded-2xl shadow-warm shrink-0"
                data-testid="founder-photo"
              />
              <div>
                <p className="font-accent text-4xl text-terracotta leading-none">Luc DiFlavio</p>
                <p className="mt-2 font-semibold text-ink">Founder &amp; Race Director</p>
                <p className="text-sm text-ink-soft">Grimsby on the Lake Turkey Trot</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 sm:px-8 pb-24 sm:pb-32">
        <Reveal>
          <div className="rounded-3xl bg-terracotta text-white px-8 py-14 sm:px-16 sm:py-20 text-center sm:text-left">
            <Kicker className="text-ochre">ready to trot?</Kicker>
            <h2 className="mt-2 font-display font-semibold tracking-tight text-4xl sm:text-5xl max-w-2xl">
              Claim your spot for {EVENT.dateLabel}.
            </h2>
            <div className="mt-8 flex flex-wrap gap-3 justify-center sm:justify-start">
              <CTAButton href={LINKS.register} variant="forest">Register Now <ArrowUpRight size={16} /></CTAButton>
              <Link to="/schedule" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full border border-white/40 text-sm font-semibold hover:bg-white/10 transition-colors duration-200">
                See race day schedule
              </Link>
            </div>
          </div>
        </Reveal>
      </section>
    </>
  );
}
